import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import { postData } from "../../../services/api";
import { Document, pdf, PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import html2canvas from "html2canvas";
import EligibilityOverviewChartPdf from "../../../pages/pdf/EligibilityOverviewChartPdf";
import { API_URL } from "../../../config/host";

const colors = [
  "#ff2d55",
  "#fbbf24",
  "#10b981",
  "#3b82f6",
  "#9ca3af",
  "#8b5cf6",
  "#f59e0b",
];

const EligibilityOverviewReport = () => {
  const chartRef = useRef(null);
  const chartRadinesssRef = useRef(null);
  const user = useSelector((state) => state.auth?.user);
  const [chartImageRetirement, setChartImageRetirement] = useState(null);
  const [chartImageReadiness, setChartImageReadiness] = useState(null);
  const [chartData, setChartData] = useState({
    windows: [],
    list: [],
    timeframe: [],
    readinessList: [],
  });
  const [retirementData, setRetirementData] = useState([]);
  const [redinessData, setRedinessData] = useState([]);

  const extractYears = (name) => {
    const nums = (name.match(/\d+/g) || []).map(Number);
    return nums.length === 1
      ? nums[0]
      : nums.length === 2
      ? (nums[0] + nums[1]) / 2
      : null;
  };

  const windowConfigs = useMemo(() => {
    return [...chartData.windows]
      .sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
      .map((w, i) => ({
        ...w,
        key: w.name,
        title: w.name,
        color: colors[i % colors.length],
        years: extractYears(w.name),
      }));
  }, [chartData.windows]);

  const grouped = useMemo(() => {
    return chartData.list.reduce((acc, person) => {
      const key = person.window || "N/A";
      (acc[key] = acc[key] || []).push(person);
      return acc;
    }, {});
  }, [chartData.list]);

  const timeframeConfigs = useMemo(() => {
    return [...chartData.timeframe]
      .sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
      .map((w, i) => ({
        ...w,
        key: w.name,
        title: w.name,
        color: colors[i % colors.length],
        years: extractYears(w.name),
      }));
  }, [chartData.windows]);

  const groupedReadiness = useMemo(() => {
    return chartData.readinessList.reduce((acc, person) => {
      const key = person.readiness || "N/A";
      (acc[key] = acc[key] || []).push(person);
      return acc;
    }, {});
  }, [chartData.readinessList]);

  const chartSeries = useMemo(
    () => windowConfigs.map((w) => grouped[w.key]?.length || 0),
    [windowConfigs, grouped]
  );
  const chartLabels = useMemo(
    () => windowConfigs.map((w) => w.title),
    [windowConfigs]
  );
  const chartColors = useMemo(
    () => windowConfigs.map((w) => w.color),
    [windowConfigs]
  );

  const readinessOptions = useMemo(
    () => timeframeConfigs.map((w) => w.title),
    [windowConfigs]
  );
  const readinessSeries = useMemo(
    () => timeframeConfigs.map((w) => groupedReadiness[w.key]?.length || 0),
    [windowConfigs, groupedReadiness]
  );
  const readinessColors = useMemo(
    () => timeframeConfigs.map((w) => w.color),
    [windowConfigs]
  );

  const loadData = useCallback(async () => {
    try {
      const [exp, retire, timeFrame, readiness] = await Promise.all([
        postData("experience/list", {
          sortQuery: { created_at: 1 },
          type: 3,
          status: 1,
          is_visabled: 1,
        }),
        postData("/retirement-overview", { isReport: 1 }),
        postData("filter-item/list", {
          sortQuery: { created_at: 1 },
          type: "timeframe",
          status: 1,
        }),
        postData("/readiness-overview", { isReport: 1 }), // adjust if different endpoint
      ]);
      setChartData({
        windows: exp.data || [],
        list: (retire.data || []).map((i) => ({
          _id: i._id,
          name: i.name,
          window: i.retirement_details?.name || "N/A",
        })),
        timeframe: timeFrame.data || [],
        readinessList: (readiness.data || []).map((i) => ({
          _id: i._id,
          name: i.name,
          readiness: i.timeframe_details?.name || "N/A",
        })),
      });
      setRetirementData(
        (retire.data || [])
          .map(({ _id, name, position, retirement_details }) => ({
            _id,
            name,
            position: position?.name || "",
            window: retirement_details?.name || "",
          }))
          .filter(({ window }) => window) // Only keep items with a window value
      );

      setRedinessData(
        (readiness.data || [])
          .map(({ _id, name, position, timeframe_details }) => ({
            _id,
            name,
            position: position?.name || "",
            readiness: timeframe_details?.name || "",
          }))
          .filter(({ readiness }) => readiness) // Only keep items with readiness value
      );
    } catch (err) {
      console.error(err.message);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pieChartOptions = (title, labels, colors) => ({
    chart: { type: "pie" },
    labels,
    colors,
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => opts.w.config.series[opts.seriesIndex],
      style: { fontSize: "20px", colors: ["#000"] },
      dropShadow: { enabled: true, blur: 3, opacity: 0.5 },
    },
    tooltip: {
      y: { formatter: (val) => `${val} employees` },
    },
    title: {
      text: title,
      align: "center",
      style: { fontSize: "20px", fontWeight: "bold" },
    },
    legend: { show: false },
    noData: {
      text: "No Data Found",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#999",
        fontSize: "16px",
      },
    },
  });

  useEffect(() => {
    if (chartRef.current && chartSeries.length > 0) {
      setTimeout(() => {
        html2canvas(chartRef.current).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          setChartImageRetirement(imgData);
        });
      }, 1000);
    }
  }, [chartSeries]);

  useEffect(() => {
    if (chartRadinesssRef.current && chartSeries.length > 0) {
      setTimeout(() => {
        html2canvas(chartRadinesssRef.current).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          setChartImageReadiness(imgData);
        });
      }, 1000);
    }
  }, [chartSeries]);

    const getCustomLegendLabels = windowConfigs?.map((item) => ({
    label: item?.title || item?.name,
    color: item?.color,
  }));

  const handleRetirementDownloadClick = async (
    e,
    title,
    pageTitle,
    chartImageUrl,
    type
  ) => {
    e.preventDefault();
    const newTab = window.open("", "_blank");

    newTab.document.write(`
    <html lang="en">
      <head>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          body {
            @apply bg-gray-100;
            margin: 0;
            height: 100vh;
          }
          .loader-container {
            @apply flex flex-col justify-center items-center h-screen;
          }
          .loader {
            width: 50px;
            aspect-ratio: 1;
            display: grid;
            border-radius: 50%;
            background:
              linear-gradient(0deg, rgb(0 0 0 / 50%) 30%, #0000 0 70%, rgb(0 0 0 / 100%) 0) 50% / 8% 100%,
              linear-gradient(90deg, rgb(0 0 0 / 25%) 30%, #0000 0 70%, rgb(0 0 0 / 75%) 0) 50% / 100% 8%;
            background-repeat: no-repeat;
            animation: l23 1s infinite steps(12);
          }
          .loader::before,
          .loader::after {
            content: "";
            grid-area: 1 / 1;
            border-radius: 50%;
            background: inherit;
            opacity: 0.915;
            transform: rotate(30deg);
          }
          .loader::after {
            opacity: 0.83;
            transform: rotate(60deg);
          }
          @keyframes l23 {
            100% {
              transform: rotate(1turn);
            }
          }
        </style>
      </head>
      <body>
        <div class="flex justify-center items-center mt-20 loader-container">
          <div class="flex flex-col items-center justify-center space-y-4">
            <div class="loader"></div>
            <p class="text-teal-700 text-lg font-semibold mt-4">Generating your PDF... Please wait.</p>
          </div>
        </div>
      </body>
    </html>
  `);

    try {
      const blob = await pdf(
        <EligibilityOverviewChartPdf
          user={user}
          companyLogo={API_URL + user?.image}
          chartImageUrl={chartImageUrl}
          title={title}
          pageTitle={pageTitle}
          pdfData={
            type && type === "retirement" ? retirementData : redinessData
          }
          type={type}
          orderIngData={
            type && type === "retirement"
              ? chartData.windows
              : chartData.timeframe
          }
          legendLabels={getCustomLegendLabels}
        />
      ).toBlob();

      const blobURL = URL.createObjectURL(blob);
      newTab.location.href = blobURL;
    } catch (error) {
      newTab.document.body.innerHTML = `
      <div class="flex flex-col items-center justify-center h-screen bg-red-100">
        <p class="text-red-700 text-lg font-semibold">Failed to load PDF. Please try again later.</p>
        <p class="text-sm text-gray-600">${error.message}</p>
      </div>
    `;
      console.error("PDF generation error:", error);
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Retirement Chart */}
        <div className="bg-white p-3 rounded-xl shadow border border-slate-200">
          <div className="flex items-end justify-end">
            <button
              type={"button"}
              className={
                "flex justify-center items-center gap-2 bg-teal-500 text-white rounded text-lg h-10 py-0 px-3 transition-all duration-200 cursor-pointer "
              }
              onClick={(e) =>
                handleRetirementDownloadClick(
                  e,
                  "Retirement Chart Overview",
                  "Company Retirement Data Report",
                  chartImageRetirement,
                  "retirement"
                )
              }
              disabled={chartImageRetirement ? false : true}
            >
              <i className={"fa-fw fa-light fa-download"}></i>
              <div className={"text-sm font-medium whitespace-nowrap"}>
                {chartImageRetirement ? "Download Report" : "Loading..."}
              </div>
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="relative" ref={chartRef}>
              <div className="text-center mb-6">
                <h3 className="text-xl md:text-4xl font-bold text-gray-800">
                  Retirement Overview
                </h3>
                {/* <div className="mt-2 text-lg font-semibold text-slate-700">
                  A visual summary of your retirement funds and allocation
                </div> */}
              </div>
              <Chart
                options={pieChartOptions(
                  "",
                  chartLabels,
                  chartColors
                )}
                series={chartSeries}
                type="pie"
                height={500}
              />
            </div>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4">
              {windowConfigs.map((w, i) => {
                let cleanedLabel = w.title;
                if (
                  cleanedLabel.startsWith("0-2Years") ||
                  cleanedLabel.startsWith("0-2 Years")
                ) {
                  cleanedLabel = "0-2 Years";
                }

                return (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: w.color }}
                    ></div>
                    <div className="text-lg font-semibold text-slate-700">
                      {cleanedLabel} ({chartSeries[i] || 0})
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Readiness Chart */}
        <div className="bg-white p-3 rounded-xl shadow border border-slate-200">
          <div className="flex items-end justify-end">
            <button
              type={"button"}
              className={
                "flex justify-center items-center gap-2 bg-teal-500 text-white rounded text-lg h-10 py-0 px-3 transition-all duration-200 cursor-pointer "
              }
              onClick={(e) =>
                handleRetirementDownloadClick(
                  e,
                  "Readiness Chart Overview",
                  "Company Readiness Breakdown Report",
                  chartImageReadiness,
                  "readiness"
                )
              }
              disabled={chartImageReadiness ? false : true}
            >
              <i className={"fa-fw fa-light fa-download"}></i>
              <div className={"text-sm font-medium whitespace-nowrap"}>
                {chartImageReadiness ? "Download Report" : "Loading..."}
              </div>
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="relative" ref={chartRadinesssRef}>
              <div className="text-center mb-6">
                <h3 className="text-xl md:text-4xl font-bold text-gray-800">Readiness Overview</h3>
                {/* <div className="mt-2 text-lg font-semibold text-slate-700">
                  A visual summary of your retirement funds and allocation
                </div> */}
              </div>
              <Chart
                options={pieChartOptions(
                  "",
                  readinessOptions,
                  readinessColors
                )}
                series={readinessSeries}
                type="pie"
                height={500}
              />
            </div>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4">
              {readinessOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: readinessColors[i] }}
                  ></div>
                  <div className="text-lg font-semibold text-slate-700">
                    {opt} ({readinessSeries[i] || 0})
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EligibilityOverviewReport;